import { InitState } from '../../reducers/root/initialState'
import { AppDispatch } from '../../../@types/store'
import { postRentValuation } from '../../../api/valuation/valuationApi'
import { toValuationPropertyType } from '../../../api/valuation/converters/propertyType'
import { selectHasRequiredAddressForValuation } from '../../selectors/insertionSelectors'
import {
  valuationRequest,
  valuationRequestCompleted,
  valuationReset,
  valuationSuccess,
} from '../../reducers/valuation/valuationFeature'
import { fetchRentControlIfNeeded } from './fetchRentControlIfNeeded'
import { getDefaultLogger } from '../../../services/logger/logger'

const VALUATION_DEBOUNCE_TIMEOUT_MS = 1000
let valuationTimer: ReturnType<typeof setTimeout>

type OptionalString = string | undefined
type OptionalNumber = number | undefined

const toOptionalNumber = (value: OptionalString): OptionalNumber => {
  if (!value) {
    return undefined
  }

  const trimmedValue = value.trim()
  if (!trimmedValue) {
    return undefined
  }

  const normalizedValue = trimmedValue.replace(',', '.')
  const parsedValue = Number(normalizedValue)
  return Number.isFinite(parsedValue) ? parsedValue : undefined
}

const createRequestFingerprint = (
  type: string,
  city: string,
  street: string,
  houseNumber: string,
  livingArea: OptionalNumber,
  numberOfRooms: OptionalNumber
) => `${type}|${city}|${street}|${houseNumber}|${livingArea ?? ''}|${numberOfRooms ?? ''}`

export const fetchRentValuationIfReady = () => async (dispatch: AppDispatch, getState: () => InitState) => {
  const { insertionFeature, flowFeature, valuationFeature } = getState()
  const { category } = flowFeature.flowInformation.metaData

  if (!selectHasRequiredAddressForValuation(getState())) {
    dispatch(valuationReset())
    return
  }

  const { street, city, houseNumber, livingSpace, room } = insertionFeature.insertionData
  const livingArea = toOptionalNumber(livingSpace)
  const numberOfRooms = toOptionalNumber(room)
  const valuationType = toValuationPropertyType(category)
  const requestPayload = {
    type: valuationType,
    address: {
      city: city!.trim(),
      street: street!.trim(),
      houseNumber: houseNumber!.trim(),
    },
    livingArea,
    numberOfRooms,
  }

  const requestFingerprint = createRequestFingerprint(
    requestPayload.type,
    requestPayload.address.city,
    requestPayload.address.street,
    requestPayload.address.houseNumber,
    requestPayload.livingArea,
    requestPayload.numberOfRooms
  )

  const isUnchangedRequest =
    valuationFeature.lastRequestFingerprint === requestFingerprint &&
    valuationFeature.maxValue !== undefined &&
    valuationFeature.minValue !== undefined &&
    valuationFeature.recommendedValue !== undefined
  if (isUnchangedRequest) {
    return
  }

  dispatch(valuationRequest())
  try {
    const valuationResult = await postRentValuation(requestPayload)
    if (!valuationResult) {
      dispatch(valuationReset())
      return
    }

    dispatch(valuationSuccess({ valuationResult, requestFingerprint }))
    await dispatch(fetchRentControlIfNeeded(requestPayload.address.city))
  } catch (error) {
    getDefaultLogger().warn('Failed to fetch rent valuation', error)
    dispatch(valuationReset())
  } finally {
    dispatch(valuationRequestCompleted())
  }
}

export const fetchRentValuationDebounced = () => (dispatch: AppDispatch) => {
  clearTimeout(valuationTimer)
  valuationTimer = setTimeout(() => {
    dispatch(fetchRentValuationIfReady())
  }, VALUATION_DEBOUNCE_TIMEOUT_MS)
}
